import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CreditCard, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

export default function PublicInvoice() {
  const { token } = useParams();
  const { setForceLightMode } = useTheme();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    setForceLightMode(true);
    return () => setForceLightMode(false);
  }, [setForceLightMode]);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvoice = useCallback(async () => {
    try {
      // Internal staff previewing (logged into the CRM, or using ?preview=1) are NOT counted as a client view
      const isInternal = typeof window !== 'undefined' &&
        (!!localStorage.getItem('token') || new URLSearchParams(window.location.search).get('preview') === '1');
      const res = await axios.get(`${API}/public/pay/${token}${isInternal ? '?internal=1' : ''}`);
      setInvoice(res.data);
    } catch (e) {
      setError('Invoice not found.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  // After returning from Stripe Checkout, verify the payment status
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    setVerifying(true);
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/public/pay/${token}/status/${sessionId}`);
        if (res.data.payment_status === 'paid' || res.data.invoice_status === 'paid') {
          setVerifying(false);
          fetchInvoice();
          return;
        }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts < 6) {
        setTimeout(poll, 2000);
      } else {
        setVerifying(false);
        fetchInvoice();
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/public/pay/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start payment.');
      setPaying(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (error && !invoice) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{error}</div>;
  }

  const isPaid = invoice.status === 'paid';
  const isCancelled = invoice.status === 'cancelled';
  const isInternalPreview = typeof window !== 'undefined' &&
    (!!localStorage.getItem('token') || new URLSearchParams(window.location.search).get('preview') === '1');
  const remaining = invoice.remaining != null
    ? invoice.remaining
    : Math.max((invoice.total || 0) - (invoice.paid_amount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="rounded-sm shadow-sm" data-testid="public-invoice-card">
          <CardHeader className="border-b">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold tracking-tight">{invoice.company_name || 'Invoice'}</p>
                <p className="text-sm text-muted-foreground mt-1">Invoice {invoice.invoice_number}</p>
              </div>
              {isPaid ? (
                <Badge className="bg-green-700" data-testid="public-invoice-paid"><CheckCircle2 className="h-3 w-3 mr-1" /> Paid</Badge>
              ) : (
                <Badge variant="outline">{invoice.status}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {isInternalPreview && (
              <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="public-internal-banner">
                Internal preview — this open is <span className="font-medium">not</span> counted as a client view.
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Billed To</p>
                <p className="font-medium">{invoice.client_name || '—'}</p>
              </div>
              <div className="text-right">
                <p className="text-muted-foreground">Due Date</p>
                <p className="font-medium">{invoice.due_date}</p>
              </div>
            </div>

            {invoice.project_name && (
              <div className="text-sm">
                <span className="text-muted-foreground">Project: </span>
                <span className="font-medium">{invoice.project_name}</span>
              </div>
            )}

            {/* Scope of work — descriptions only, no per-line pricing */}
            <div>
              <p className="text-sm font-medium mb-2">Scope of Work</p>
              <ul className="border rounded-sm divide-y">
                {invoice.line_items?.map((li, i) => (
                  <li key={i} className="px-3 py-2 text-sm" data-testid={`public-line-${i}`}>{li.description}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmt(invoice.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax ({invoice.tax_rate}%)</span><span>{fmt(invoice.tax_amount)}</span></div>
              <div className="flex justify-between font-bold text-base border-t pt-2"><span>Total</span><span>{fmt(invoice.total)}</span></div>
              {invoice.paid_amount > 0 ? (
                <>
                  <div className="flex justify-between text-green-700"><span>Paid</span><span>-{fmt(invoice.paid_amount)}</span></div>
                  {remaining > 0 && (
                    <div className="flex justify-between font-bold text-blue-700"><span>Balance Due</span><span>{fmt(remaining)}</span></div>
                  )}
                </>
              ) : (
                invoice.amount_due !== invoice.total && (
                  <div className="flex justify-between font-bold text-blue-700"><span>Amount Due</span><span>{fmt(invoice.amount_due)}</span></div>
                )
              )}
            </div>

            {invoice.notes && (
              <div className="text-sm bg-secondary/40 rounded-sm p-3" data-testid="public-invoice-notes">
                <p className="text-muted-foreground mb-1">Notes</p>
                <p className="whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}

            {verifying && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground p-3 bg-blue-50 rounded-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            {isPaid ? (
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-sm text-green-800 font-medium" data-testid="public-thankyou">
                Thank you — this invoice is paid in full.
              </div>
            ) : isCancelled ? (
              <div className="text-center p-4 bg-slate-100 border rounded-sm text-muted-foreground" data-testid="public-cancelled">
                This invoice has been cancelled. Please contact {invoice.company_name || 'us'} with any questions.
              </div>
            ) : (
              <div className="space-y-4">
                {invoice.can_pay_online && remaining > 0 && (
                  <Button className="w-full h-12 text-base" onClick={handlePay} disabled={paying || verifying}
                    data-testid="public-pay-btn">
                    <CreditCard className="h-5 w-5 mr-2" />
                    {paying ? 'Redirecting to secure checkout…' : `Pay ${fmt(remaining)} by card`}
                  </Button>
                )}

                {invoice.venmo_username && remaining > 0 && (
                  <div className={`${invoice.can_pay_online ? 'border-t pt-4' : ''} space-y-3`} data-testid="public-venmo-section">
                    {invoice.can_pay_online && (
                      <p className="text-xs text-center text-muted-foreground">or pay with Venmo</p>
                    )}
                    <a href={invoice.venmo_link} target="_blank" rel="noreferrer"
                      className="flex items-center justify-center gap-2 w-full h-12 rounded-md bg-[#3D95CE] hover:bg-[#3486bb] text-white font-medium transition-colors"
                      data-testid="public-venmo-btn">
                      Pay {fmt(remaining)} with Venmo
                    </a>
                    {invoice.venmo_qr && (
                      <div className="flex flex-col items-center gap-1">
                        <img src={invoice.venmo_qr} alt="Venmo QR code" className="h-32 w-32" data-testid="public-venmo-qr" />
                        <p className="text-[11px] text-center text-muted-foreground max-w-xs">
                          Scan with your phone to pay in the Venmo app. {invoice.company_name || 'Your contractor'} will confirm and mark this invoice paid.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {!invoice.can_pay_online && !invoice.venmo_username && remaining > 0 && (
                  <p className="text-sm text-center text-muted-foreground">
                    Online payment isn’t available for this invoice. Please contact {invoice.company_name || 'us'} to arrange payment.
                  </p>
                )}

                {remaining <= 0 && (
                  <div className="text-center p-4 bg-green-50 border border-green-200 rounded-sm text-green-800 font-medium" data-testid="public-fullypaid">
                    This invoice is fully paid. Thank you!
                  </div>
                )}
              </div>
            )}

            {invoice.can_pay_online && <p className="text-[11px] text-center text-muted-foreground">Card payments secured by Stripe</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
